import { MAX_COLOR_OPTIONS, type ProductDetail } from '@repo/api-contracts';
import { type ReactElement, useEffect, useRef, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useInvalidateProducts } from '@/entities/product';
import { type ResettableRequest, resetSettledRequests } from '@/shared/lib';
import { Button } from '@/shared/ui';
import { type ColorFormValues, NEW_COLOR_ROW } from '../model/color-form';
import { useSetColorOptions } from '../model/use-set-color-options';
import { useImageUpload } from '../upload/use-image-upload';
import { ColorRow } from './color-row';
import { ColorSwatchStrip } from './color-swatch-strip';
import { SectionCard } from './section-card';

interface ColorSectionProps {
  readonly productId: string;
  readonly colorOptions: ProductDetail['colorOptions'];
  readonly onSaved: (message: string) => void;
}

/** 색상 옵션 — 목록에서 빠진 기존 행은 저장 시 삭제된다(동기화). */
export const ColorSection = ({
  productId,
  colorOptions,
  onSaved,
}: ColorSectionProps): ReactElement => {
  const invalidate = useInvalidateProducts();
  const {
    upload,
    isUploading,
    error: uploadError,
    clearError: clearUploadError,
  } = useImageUpload(productId);
  const saveAction = useSetColorOptions({
    onSuccess: () => {
      onSaved('색상 옵션을 저장했습니다.');
      invalidate.detail(productId);
    },
  });
  const uploadRequest: ResettableRequest = { isPending: isUploading, reset: clearUploadError };

  // 미리보기 표시 URL — 키 → URL. 저장된 행은 서버 presigned URL, 새 업로드는 로컬 URL.
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      colorOptions
        .filter((option) => option.previewImage !== null)
        .map((option) => [option.previewImage?.key ?? '', option.previewImage?.url ?? '']),
    ),
  );

  const objectUrlsRef = useRef<string[]>([]);
  useEffect(
    () => () => {
      for (const url of objectUrlsRef.current) {
        URL.revokeObjectURL(url);
      }
    },
    [],
  );

  const { register, control, handleSubmit, setValue, watch } = useForm<ColorFormValues>({
    defaultValues: {
      colorOptions: colorOptions.map((option) => ({
        optionId: option.id,
        name: option.name,
        colorCode: option.colorCode,
        colorFamily: option.colorFamily,
        isAvailable: option.isAvailable,
        previewImageKey: option.previewImage?.key ?? null,
      })),
    },
  });
  const { fields, append, remove } = useFieldArray({ control, name: 'colorOptions' });

  // 스와치 스트립·수급 뱃지 표시용 실시간 값.
  const rows = watch('colorOptions');

  // 미리보기 업로드와 색상 저장은 오류 한 줄을 함께 쓴다. 요청을 보내기 직전에 다른 쪽의 지난 오류를
  // 지운다 — 아니면 옛 업로드 오류가 뒤의 저장 거부를 가리고, 옛 저장 오류가 뒤의 업로드 성공 뒤에도 남는다.
  // 진행 중인 요청은 건드리지 않는다(resetSettledRequests). 저장은 칸 검사를 통과해야 여기에 온다.
  // 업로드가 사전 검사(형식·크기)에 막히면 그 문구가 오류 한 줄의 앞자리에 올라, 먼저 지운 저장 오류는
  // 어차피 보이지 않는다.
  const onSubmit = (values: ColorFormValues): void => {
    resetSettledRequests([uploadRequest]);
    saveAction.setColorOptions({
      id: productId,
      colorOptions: values.colorOptions.map(({ optionId, ...rest }) =>
        optionId === undefined ? rest : { id: optionId, ...rest },
      ),
    });
  };

  const handlePreviewUpload = (index: number, file: File): void => {
    resetSettledRequests([saveAction]);
    void upload(file, 'COLOR_PREVIEW').then((key) => {
      if (key) {
        const localUrl = URL.createObjectURL(file);
        objectUrlsRef.current.push(localUrl);
        setPreviewUrls((current) => ({ ...current, [key]: localUrl }));
        setValue(`colorOptions.${index}.previewImageKey`, key);
      }
    });
  };

  return (
    <SectionCard
      title="색상 옵션"
      description="스와치는 색상 코드로 렌더링되고, 필터는 표준 색상군으로만 동작합니다. 수급 불가 색상은 사용자 화면에서 선택할 수 없게 표시됩니다."
      error={uploadError ?? saveAction.error?.message ?? null}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        {rows.length > 0 ? (
          <ColorSwatchStrip rows={rows} fieldIds={fields.map((field) => field.id)} />
        ) : null}

        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left">
              <tr>
                <th className="px-3 py-2 font-medium">스와치</th>
                <th className="px-3 py-2 font-medium">색상명</th>
                <th className="px-3 py-2 font-medium">색상군(필터)</th>
                <th className="px-3 py-2 font-medium">수급 상태</th>
                <th className="px-3 py-2 font-medium">색상 사진</th>
                <th className="px-3 py-2 font-medium">관리</th>
              </tr>
            </thead>
            <tbody>
              {fields.map((field, index) => {
                const previewKey = watch(`colorOptions.${index}.previewImageKey`);

                return (
                  <ColorRow
                    key={field.id}
                    index={index}
                    register={register}
                    isAvailable={rows[index]?.isAvailable ?? true}
                    previewKey={previewKey}
                    previewUrl={previewKey ? (previewUrls[previewKey] ?? null) : null}
                    isUploading={isUploading}
                    onPreviewUpload={handlePreviewUpload}
                    onPreviewClear={(rowIndex) =>
                      setValue(`colorOptions.${rowIndex}.previewImageKey`, null)
                    }
                    onRemove={remove}
                  />
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={fields.length >= MAX_COLOR_OPTIONS}
            onClick={() => append({ ...NEW_COLOR_ROW })}
          >
            + 색상 추가
          </Button>
          <Button type="submit" size="sm" isLoading={saveAction.isPending}>
            색상 저장
          </Button>
        </div>
      </form>
    </SectionCard>
  );
};
